/**
 * Impresión de tickets térmicos vía HTML + CSS @page.
 *
 * Bypasea el flujo PDF para evitar que el navegador/driver inserte una "página"
 * tradicional antes del contenido. El truco es usar:
 *   @page { size: 80mm auto; margin: 0; }
 *
 * Esto le indica al navegador que el papel es de 80mm de ancho y altura automática
 * según el contenido. El driver con papel continuo lo respeta y NO avanza
 * ningún papel inicial antes de empezar a imprimir.
 */

const money = (v) => {
  // Formato DGII (Ley 32-23 / Informe Técnico v1.0): punto decimal, SIN separador de miles.
  const n = Number(v || 0);
  return n.toFixed(2);
};
const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("es-DO", {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch (_) { return String(iso).slice(0, 16); }
};
const truncate = (s, n) => (!s ? "" : s.length <= n ? s : s.slice(0, n - 1) + "…");

const TITLES = {
  invoice: "FACTURA DE VENTA",
  credit_note: "NOTA DE CRÉDITO",
  debit_note: "NOTA DE DÉBITO",
  quotation: "COTIZACIÓN",
  delivery_note: "CONDUCE / NOTA DE ENTREGA",
  payment: "RECIBO DE PAGO",
};

function renderItemsRows(items) {
  return items.map((it) => {
    const qty = Number(it.quantity || 0);
    const price = Number(it.price || 0);
    const disc = Number(it.discount || 0);
    const line = qty * price - disc;
    return `
      <tr>
        <td class="desc">
          ${it.sku ? `<b>${it.sku}</b><br>` : ""}${truncate(it.name || "", 50)}
        </td>
        <td class="qty">${qty}</td>
        <td class="num">${money(price)}</td>
        <td class="num">${money(line)}</td>
      </tr>`;
  }).join("");
}

function buildTicketHTML(doc, company, kind, dgii) {
  const title = TITLES[kind] || "DOCUMENTO";
  const showItems = kind !== "payment";
  const showTotals = kind !== "delivery_note" && kind !== "payment";
  dgii = dgii || {};

  let itemsBlock = "";
  if (showItems) {
    itemsBlock = `
      <table class="items">
        <thead>
          <tr>
            <th class="desc">Desc.</th>
            <th class="qty">Cant</th>
            <th class="num">Precio</th>
            <th class="num">Total</th>
          </tr>
        </thead>
        <tbody>${renderItemsRows(doc.items || [])}</tbody>
      </table>`;
  } else {
    // Recibo de pago
    const ref = doc.reference_number || doc.invoice_number || doc.purchase_number || doc.reference_id || "";
    const refLabel = doc.type === "payable" || doc.purchase_number ? "Compra" : "Factura";
    itemsBlock = `
      <table class="kv">
        ${ref ? `<tr><th>${refLabel}:</th><td>${ref}</td></tr>` : ""}
        <tr><th>Método:</th><td>${(doc.method || doc.payment_method || "").toUpperCase()}</td></tr>
        <tr><th>Monto:</th><td>RD$ ${money(doc.amount)}</td></tr>
        ${doc.reference ? `<tr><th>Ref:</th><td>${truncate(doc.reference, 24)}</td></tr>` : ""}
        ${doc.notes ? `<tr><th>Nota:</th><td>${truncate(doc.notes, 60)}</td></tr>` : ""}
      </table>`;
  }

  let totalsBlock = "";
  if (showTotals) {
    totalsBlock = `
      <table class="totals">
        <tr><td class="label">Subtotal:</td><td class="num">RD$ ${money(doc.subtotal)}</td></tr>
        ${doc.discount > 0 ? `<tr><td class="label">Descuento:</td><td class="num">− RD$ ${money(doc.discount)}</td></tr>` : ""}
        <tr><td class="label">ITBIS:</td><td class="num">RD$ ${money(doc.itbis)}</td></tr>
        <tr class="grand"><td class="label">TOTAL:</td><td class="num">RD$ ${money(doc.total)}</td></tr>
      </table>`;
  }

  // Referencia para notas/conduce vinculado a factura
  let refBlock = "";
  if ((kind === "credit_note" || kind === "debit_note") && doc.invoice_number) {
    refBlock = `
      <div class="block">
        <b>Factura ref:</b> ${doc.invoice_number} (${doc.invoice_ncf || ""})
        ${doc.reason ? `<br><b>Motivo:</b> ${truncate(doc.reason, 80)}` : ""}
      </div>`;
  } else if (kind === "delivery_note" && doc.invoice_number) {
    refBlock = `<div class="block"><b>Factura ref:</b> ${doc.invoice_number}</div>`;
  }

  const deliveryExtras = (kind === "delivery_note") ? `
    ${doc.delivery_address ? `<div class="block"><b>Dirección:</b> ${truncate(doc.delivery_address, 80)}</div>` : ""}
    ${doc.driver ? `<div class="block"><b>Chofer:</b> ${doc.driver}</div>` : ""}
    ${doc.vehicle ? `<div class="block"><b>Vehículo:</b> ${doc.vehicle}</div>` : ""}
    <div class="sigs">_______________________________<br>Firma de recibido</div>
  ` : "";

  const invoicePayInfo = (kind === "invoice") ? `
    <div class="block">
      <b>Pago:</b> ${(doc.payment_method || "").toUpperCase()} |
      <b>Estado:</b> ${(doc.payment_status || "").toUpperCase()}
    </div>
    ${doc.balance_due > 0 ? `<div class="block"><b>SALDO: RD$ ${money(doc.balance_due)}</b></div>` : ""}
  ` : "";

  // Bloque DGII — obligatorio para e-CF (Ley 32-23 / Norma 06-2018)
  const codModMap = { "1": "Anula", "2": "Corrige texto", "3": "Corrige monto" };
  const dgiiBlock = (doc.is_electronic && dgii.qr_png_b64) ? `
    <hr class="sep">
    ${dgii.ncf_vence ? `<div class="block small"><b>Vence NCF:</b> ${dgii.ncf_vence}</div>` : ""}
    ${dgii.security_code ? `<div class="block small"><b>Cód. Seguridad:</b> ${dgii.security_code}</div>` : ""}
    ${dgii.signed_at ? `<div class="block small"><b>Fecha/Hora Firma:</b> ${dgii.signed_at}</div>` : ""}
    ${dgii.ncf_modificado ? `
      <div class="block small">
        <b>Modifica:</b> ${dgii.ncf_modificado} (${dgii.fecha_ncf_modificado || ""})
        ${dgii.codigo_modificacion ? `<br/><b>Código ${dgii.codigo_modificacion}:</b> ${codModMap[dgii.codigo_modificacion] || ""}` : ""}
      </div>` : ""}
    <div class="qr-block">
      <img class="qr" src="data:image/png;base64,${dgii.qr_png_b64}" alt="QR DGII"/>
      <div class="small">Valida en DGII escaneando el QR</div>
    </div>
  ` : "";

  // Marca de agua "SIN VALOR FISCAL" si rechazado
  const rejected = !!dgii.rejected;
  const watermarkCss = rejected ? `
    .wm { position: fixed; top: 40%; left: 50%; transform: translate(-50%,-50%) rotate(-25deg);
          font-size: 24px; font-weight: 900; color: rgba(220,30,30,0.35);
          letter-spacing: 2px; text-align: center; pointer-events: none; z-index: 9999; }
    .wm small { font-size: 10px; font-weight: 700; display: block; letter-spacing: 1px; }
  ` : "";
  const watermarkHtml = rejected ?
    `<div class="wm">SIN VALOR FISCAL<small>Rechazado por DGII</small></div>` : "";

  return `<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="utf-8">
<title>${doc.number || title}</title>
<style id="page-style">
  /* Se actualizará con altura exacta después del render */
  @page {
    size: 80mm auto;
    margin: 0;
  }
  * { box-sizing: border-box; }
  html, body {
    width: 80mm;
    margin: 0;
    padding: 0;
    font-family: 'Helvetica Neue', Arial, sans-serif;
    color: #000;
    background: #fff;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  body {
    padding: 2mm 3mm 3mm 3mm;
    font-size: 11px;
    line-height: 1.3;
  }
  /* Eliminar cualquier margen/padding implícito que pueda agregar el navegador */
  @media print {
    html, body {
      width: 80mm !important;
      margin: 0 !important;
      padding-top: 0 !important;
      padding-bottom: 0 !important;
    }
    body {
      padding-left: 3mm;
      padding-right: 3mm;
    }
  }
  .center { text-align: center; }
  .bold { font-weight: 700; }
  .small { font-size: 9px; }
  .biz-name { font-size: 14px; font-weight: 700; }
  .doc-title { font-size: 13px; font-weight: 700; margin-top: 3px; }
  hr.sep { border: 0; border-top: 1px dashed #000; margin: 4px 0; }
  .block { margin: 2px 0; word-wrap: break-word; }
  table { width: 100%; border-collapse: collapse; }
  table.items th, table.items td {
    padding: 2px 0;
    vertical-align: top;
    font-size: 11px;
  }
  table.items thead th {
    border-bottom: 1px solid #000;
    text-align: left;
    font-weight: 700;
  }
  table.items td.qty, table.items th.qty,
  table.items td.num, table.items th.num { text-align: right; }
  table.items td.desc { width: 50%; }
  table.kv th { text-align: left; padding: 2px 4px 2px 0; font-weight: 700; }
  table.kv td { text-align: right; padding: 2px 0; }
  table.totals { margin-top: 4px; }
  table.totals td { padding: 1px 0; }
  table.totals td.label { text-align: right; padding-right: 6px; }
  table.totals td.num { text-align: right; width: 50%; }
  table.totals tr.grand td {
    border-top: 1px solid #000;
    padding-top: 3px;
    font-weight: 700;
    font-size: 13px;
  }
  .sigs {
    margin-top: 10mm;
    text-align: center;
    font-size: 9px;
  }
  .footer {
    margin-top: 4mm;
    text-align: center;
    font-size: 9px;
  }
  .qr-block { text-align: center; margin: 4mm 0 2mm; }
  .qr-block img.qr { width: 32mm; height: 32mm; display: block; margin: 0 auto 1mm; }
  ${watermarkCss}
</style>
</head>
<body>
  ${watermarkHtml}
  <div id="ticket-content">
  <div class="center biz-name">${truncate(company.name || "FERRETERÍA", 40)}</div>
  ${company.address ? `<div class="center small">${truncate(company.address, 80)}</div>` : ""}
  ${company.phone ? `<div class="center small">Tel: ${company.phone}</div>` : ""}
  ${company.rnc ? `<div class="center small">RNC: ${company.rnc}</div>` : ""}
  ${company.email ? `<div class="center small">${truncate(company.email, 50)}</div>` : ""}

  <hr class="sep">

  <div class="center doc-title">${title}</div>
  ${doc.number ? `<div class="center bold">No. ${doc.number}</div>` : ""}
  ${doc.ncf ? `<div class="center small">NCF: ${doc.ncf}</div>` : ""}
  ${doc.ncf_type ? `<div class="center small">Tipo: ${doc.ncf_type}</div>` : ""}
  <div class="center small">Fecha: ${fmtDate(doc.created_at)}</div>

  <hr class="sep">

  ${refBlock}

  <div class="block"><b>Cliente:</b> ${truncate(doc.customer_name || "Cliente Genérico", 40)}</div>
  ${doc.customer_rnc_cedula ? `<div class="block small">RNC/Ced: ${doc.customer_rnc_cedula}</div>` : ""}

  ${deliveryExtras}

  <hr class="sep">

  ${itemsBlock}
  ${totalsBlock}

  ${invoicePayInfo}

  ${dgiiBlock}

  ${kind === "quotation" && doc.valid_until ? `<div class="block small">Válida hasta: ${fmtDate(doc.valid_until)}</div>` : ""}

  ${doc.notes && kind !== "payment" ? `<div class="block small"><b>Notas:</b> ${truncate(doc.notes, 120)}</div>` : ""}

  <hr class="sep">

  <div class="footer">Gracias por su compra<br>${truncate(company.name || "Ferretería El Bodegón", 40)}</div>
  </div>
</body>
</html>`;
}

/**
 * Imprime un ticket usando HTML+CSS @page — NO descarga PDF.
 *
 * @param {Object} params
 * @param {Object} params.doc      — datos del documento (factura/cotización/etc)
 * @param {Object} params.company  — datos de la empresa
 * @param {string} params.kind     — invoice|credit_note|debit_note|quotation|delivery_note|payment
 * @returns {Promise<void>}
 */
export function printTicketHTML({ doc, company, kind, dgii }) {
  return new Promise((resolve) => {
    const html = buildTicketHTML(doc, company, kind, dgii);
    // El iframe DEBE tener dimensiones reales (no 0x0) para que el navegador
    // calcule correctamente la altura del documento. Si lo hacemos 0x0 Chrome
    // termina imprimiendo páginas enormes por defecto.
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.title = "Ticket de impresión";
    Object.assign(iframe.style, {
      position: "fixed",
      left: "-9999px",        // fuera de pantalla, NO oculto en CSS
      top: "0",
      width: "80mm",          // ancho real del ticket
      height: "auto",         // altura natural según contenido
      minHeight: "1px",
      border: "0",
      visibility: "visible",  // crítico: NO ocultarlo con visibility/display
    });

    let printed = false;
    let cleanedUp = false;
    const cleanup = () => {
      if (cleanedUp) return;
      cleanedUp = true;
      try { document.body.removeChild(iframe); } catch (_) { /* noop */ }
      resolve();
    };

    const tryPrint = () => {
      if (printed) return;
      printed = true;
      try {
        const win = iframe.contentWindow;
        const doc2 = iframe.contentDocument;
        if (!win || !doc2) { cleanup(); return; }

        // Calcular altura exacta del contenido renderizado.
        // Usamos getBoundingClientRect del wrapper #ticket-content para mayor precisión.
        const wrapper = doc2.getElementById("ticket-content");
        let contentH = 0;
        if (wrapper) {
          const rect = wrapper.getBoundingClientRect();
          // rect.bottom incluye el offset top del body (padding) + altura del contenido
          contentH = Math.ceil(rect.bottom);
        }
        if (!contentH) {
          contentH = doc2.body?.scrollHeight || doc2.documentElement?.scrollHeight || 200;
        }
        // Convertir px → mm (1mm ≈ 3.7795 px a 96 dpi)
        const heightMm = Math.max(20, Math.ceil(contentH / 3.7795) + 2); // +2mm de holgura

        // Inyectar @page con altura EXACTA — algunos drivers ignoran 'auto' y aplican
        // el tamaño de papel completo del driver. Forzando size: 80mm Xmm garantiza
        // que SOLO ese rectángulo se imprime.
        const pageStyle = doc2.getElementById("page-style");
        if (pageStyle) {
          pageStyle.textContent = pageStyle.textContent.replace(
            /@page\s*\{[^}]*\}/,
            `@page { size: 80mm ${heightMm}mm; margin: 0; }`,
          );
        }

        // Actualizar también height del iframe (no afecta el print pero ayuda al layout)
        iframe.style.height = `${contentH}px`;

        // Esperar un frame adicional para que el browser termine layout con el nuevo @page
        setTimeout(() => {
          try { win.focus(); } catch (_) { /* noop */ }
          try { win.print(); } catch (_) { /* noop */ }
          // No quitar el iframe inmediatamente — el diálogo de impresión
          // requiere que siga vivo. Cleanup tras varios segundos.
          setTimeout(cleanup, 8000);
        }, 80);
      } catch (e) {
        cleanup();
      }
    };

    iframe.onload = () => setTimeout(tryPrint, 100);
    // Fallback: si onload no se dispara (raro pero posible)
    setTimeout(tryPrint, 1500);

    // srcdoc dispara onload de forma confiable
    iframe.srcdoc = html;
    document.body.appendChild(iframe);
  });
}

export { buildTicketHTML };
